/**
 * React Query hooks for contact tags (ADR-141 §4).
 *
 * Key factories live in `useContacts.ts` (single definition point; this
 * module imports them). Tag writes invalidate BOTH scopes: the contact
 * list renders `tag_name` chips, so a rename or delete must refetch
 * contacts as well as the tag list itself.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContactTag,
  deleteContactTag,
  getContactTags,
  patchContactTag,
  type ContactTag,
  type ContactTagPatchPayload,
  type ContactTagWritePayload,
} from '../../services/contacts.service';
import { contactKeys, contactTagKeys } from './useContacts';

export function useContactTags() {
  return useQuery({
    queryKey: contactTagKeys.list(),
    queryFn: getContactTags,
  });
}

function useInvalidateTagScopes() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: contactTagKeys.all });
    queryClient.invalidateQueries({ queryKey: contactKeys.all });
  };
}

export function useCreateContactTag() {
  const invalidate = useInvalidateTagScopes();
  return useMutation<ContactTag, Error, ContactTagWritePayload>({
    // Wrapped so React Query's context second-arg never leaks into the
    // service signature.
    mutationFn: (body) => createContactTag(body),
    onSuccess: invalidate,
  });
}

export function usePatchContactTag() {
  const invalidate = useInvalidateTagScopes();
  return useMutation<
    ContactTag,
    Error,
    { tagId: number; body: ContactTagPatchPayload }
  >({
    mutationFn: ({ tagId, body }) => patchContactTag(tagId, body),
    onSuccess: invalidate,
  });
}

export function useDeleteContactTag() {
  const invalidate = useInvalidateTagScopes();
  return useMutation<void, Error, number>({
    mutationFn: (tagId) => deleteContactTag(tagId),
    onSuccess: invalidate,
  });
}
